import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { SelectOption, SnackbarService } from '@bcl/design-system';
import { NonconformityApiService } from './nonconformity.service';
import { DictionaryItem, Nonconformity } from './nonconformity.model';
import { CurrentUserService, IUser } from '@ews/users-management';
import { Platform } from '@ionic/angular';
import { BackButtonService } from 'src/app/services/back-button.service';

type NonconformityForm = Omit<Nonconformity, 'id' | 'reporter'> & {
  reporterId: string;
  reporterName: string;
  reporterChannel?: string;
  reporterPhone?: string;
};

@Component({
  selector: 'app-nonconformity',
  templateUrl: './nonconformity.component.html',
  styleUrls: ['./nonconformity.component.scss'],
})
export class NonconformityPage implements OnInit, OnDestroy {
  public loading = true;
  public form: FormGroup;
  public user: IUser | null = null;
  public backUrl = '';
  public incidentTypeOptions: SelectOption[] = [];
  public statusOptions: SelectOption[] = [];
  public severityOptions: SelectOption[] = [];
  public divisionOptions: SelectOption[] = [];
  public categoryOptions: SelectOption[] = [];
  public routerState: any;
  public visitId: number;
  public date: string;
  public dayNumber: string;
  public areaId: number;
  public customerId: number;
  public locationId: number;
  public parentObject: string;
  public parentId: number;
  private readonly destroyed$ = new Subject<void>();
  private nonconformityId?: number;
  private backButtonSubscription: Subscription;
  constructor(
    private readonly nonconformityService: NonconformityApiService,
    private readonly currentUserService: CurrentUserService,
    private readonly snackbarService: SnackbarService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly platform: Platform,
    private readonly backButtonService: BackButtonService
  ) {
    this.backButtonSubscription =
      this.platform.backButton.subscribeWithPriority(10, () => {
        this.goBack();
      });

    this.loading = true;
    this.loadDictionaries();
    this.route.params.subscribe((params) => {
      this.routerState = this.router.getCurrentNavigation().extras.state;
      this.visitId = params?.id;
      this.date = params?.date;
      this.dayNumber = params?.dayId;
      this.areaId = this.routerState?.areaid;
      this.customerId = this.routerState?.customerId;
      this.locationId = this.routerState?.locationId;
      this.nonconformityId = this.routerState?.nonconformityId;
      this.parentObject = this.routerState?.parentObject;
      this.parentId = this.routerState?.parentId;
      this.backUrl = this.routerState?.backUrl;
      this.currentUserService.user.subscribe((user) => {
        this.user = user;
      });
      if (this.nonconformityId && this.nonconformityId !== 0) {
        this.loadNonconformity();
      } else {
        const now = new Date();
        const currentDate = now.toISOString().split('T')[0];
        const currentTime = now
          .toLocaleTimeString('en-US', { hour12: false })
          .substring(0, 5);
        this.form.patchValue({
          date: currentDate,
          time: currentTime,
        });
        this.currentUserService.user
          .pipe(takeUntil(this.destroyed$))
          .subscribe((user) => {
            this.user = user;
            if (user) {
              this.form.patchValue({
                reporterId: user.id,
                reporterName: user.userName,
              });
            }
          });
      }
    });

    this.form = this.fb.group({
      incidentTypeId: [null, Validators.required],
      statusId: [null, Validators.required],
      severityId: [null, Validators.required],
      categoryId: [null, Validators.required],
      reporterId: [null],
      reporterName: [{ value: null, disabled: true }],
      reporterChannel: [null],
      reporterPhone: [null],
      date: [null, Validators.required],
      time: [null, Validators.required],
      title: [null, Validators.required],
      description: [null, Validators.required],
      resolvedImmediately: [false],
      immediateSolution: [null],
      materialDamage: [false],
      materialDamageDescription: [null],
      divisionId: [null, Validators.required],
      categoryOther: [null],
      causedByEWS: [false],
      materialDamageCost: [null, [Validators.min(0), this.decimalValidator()]],
    });
  }

  public get isEdit(): boolean {
    return !!this.nonconformityId;
  }

  public get showImmediateSolution(): boolean {
    return this.form.get('resolvedImmediately')?.value === true;
  }

  public get showMaterialDamageDescription(): boolean {
    return this.form.get('materialDamage')?.value === true;
  }

  ionViewWillEnter(): void {
    this.backButtonService.showBackButton({
      show: true,
      action: () => {
        this.goBack();
      },
    });
  }

  public ngOnInit(): void {
    this.backButtonService.showBackButton({
      show: true,
      action: () => {
        this.goBack();
      },
    });
  }

  public ngOnDestroy(): void {
    this.backButtonSubscription?.unsubscribe();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public applyChange(): void {
    if (this.form.invalid) {
      return;
    }

    const formValue = this.form.getRawValue();

    const nonconformity: Nonconformity = {
      ...formValue,
      id: this.nonconformityId,
    };
    nonconformity.time = formValue.time.substring(0, 5);
    if (this.nonconformityId && this.nonconformityId !== 0) {
      this.nonconformityService
        .updateNonconformity(nonconformity)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.snackbarService.open(
              'green',
              'NONCONFORMITY.MESSAGES.UPDATE_SUCCESS'
            );
          },
          error: () => {
            this.snackbarService.open(
              'red',
              'NONCONFORMITY.MESSAGES.UPDATE_ERROR'
            );
          },
        });
    } else {
      nonconformity.id = null;
      nonconformity.locationId = this.locationId?.toString();
      nonconformity.customerId = this.customerId?.toString();
      this.nonconformityService
        .createNonconformity(nonconformity)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (response) => {
            this.nonconformityId = response.id;
            nonconformity.id = this.nonconformityId;
            this.snackbarService.open(
              'green',
              'NONCONFORMITY.MESSAGES.CREATE_SUCCESS'
            );
          },
          error: () => {
            this.snackbarService.open(
              'red',
              'NONCONFORMITY.MESSAGES.CREATE_ERROR'
            );
          },
        });
    }
  }

  public goBack(): void {
    if (this.backUrl) {
      this.router
        .navigateByUrl('/notes', { skipLocationChange: true })
        .then(() => {
          this.router.navigateByUrl(`${this.backUrl}`);
        });
    } else {
      this.router.navigate(['../'], { relativeTo: this.route });
    }
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  decimalValidator() {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;

      if (value !== null && value !== undefined) {
        const decimalRegex = /^\d+(\.\d{1,2})?$/;
        return decimalRegex.test(value.toString())
          ? null
          : { invalidDecimal: true };
      }

      return null;
    };
  }

  private loadNonconformity(): void {
    if (!this.nonconformityId) {
      return;
    }

    this.nonconformityService
      .getNonconformity(this.nonconformityId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (nonconformity) => {
          const formValue: NonconformityForm = {
            ...nonconformity,
            statusId: nonconformity.statusId.toString() || null,
            incidentTypeId: nonconformity.incidentTypeId.toString() || null,
            severityId: nonconformity.severityId.toString() || null,
            divisionId: nonconformity.divisionId.toString() || null,
            categoryId: nonconformity.categoryId.toString() || null,
            reporterId: nonconformity.reporterId,
            reporterName: nonconformity.reporterName,
            reporterChannel: nonconformity.reporterChannel,
            reporterPhone: nonconformity.reporterPhone,
          };
          this.form.patchValue(formValue);
        },
        error: () => {
          this.snackbarService.open('red', 'NONCONFORMITY.MESSAGES.LOAD_ERROR');
        },
      });
  }

  private loadDictionaries(): void {
    this.nonconformityService
      .getDictionaries()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (dictionaries) => {
          this.incidentTypeOptions = this.mapDictionaryToOptions(
            dictionaries.incidentTypes
          );
          this.statusOptions = this.mapDictionaryToOptions(
            dictionaries.statuses
          );
          this.severityOptions = this.mapDictionaryToOptions(
            dictionaries.severities
          );
          this.divisionOptions = this.mapDictionaryToOptions(
            dictionaries.divisions
          );
          this.categoryOptions = this.mapDictionaryToOptions(
            dictionaries.categories
          );
          this.loading = false;
        },
        error: () => {
          this.snackbarService.open(
            'red',
            'NONCONFORMITY.MESSAGES.LOAD_DICTIONARIES_ERROR'
          );
          this.loading = false;
        },
      });
  }

  private mapDictionaryToOptions(items: DictionaryItem[]): SelectOption[] {
    return items.map((item) => ({
      value: item.value,
      label: item.name,
    }));
  }
}
